/**
 * Add To Cart Use Case
 * 
 * Application use case for adding a product variant to the cart.
 */

import { ICartService } from '@/domain/services/cart.service.interface';
import { CartItemEntity } from '@/domain/entities/cart-item.entity';

export interface AddToCartInput {
  cartToken?: string;
  productId: string;
  variantId: string;
  quantity: number;
  options?: {
    countryCode?: string;
    currency?: string;
    currencyRatio?: number;
    vendor?: string;
    shop?: string;
    upsellFunnelId?: string;
    upsellFirstStepId?: string;
  };
}

export interface AddToCartOutput {
  success: boolean;
  cartToken: string;
}

export class AddToCartUseCase {
  constructor(private readonly cartService: ICartService) {}

  private shouldRecoverWithFreshCart(message: string): boolean {
    return (
      message.includes('Cart not found') ||
      message.includes('400') ||
      message.includes('404') ||
      message.includes('409') ||
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504')
    );
  }

  async execute(input: AddToCartInput): Promise<AddToCartOutput> {
    if (!input.productId) {
      throw new Error('Product ID is required');
    }

    if (!input.variantId) {
      throw new Error('Variant ID is required');
    }

    if (!input.quantity || input.quantity < 1) {
      throw new Error('Quantity must be at least 1');
    }

    const cartItem: CartItemEntity = {
      product_id: input.productId,
      variant_id: input.variantId,
      quantity: input.quantity,
    };

    if (input.cartToken) {
      // Try adding to existing cart; if it no longer exists, create a new one
      try {
        const result = await this.cartService.addToCart(input.cartToken, cartItem, input.options);
        return result;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        if (this.shouldRecoverWithFreshCart(message)) {
          // Stale token or cart-path failure on an old cart — create a fresh cart with just this item.
          const result = await this.cartService.createCartWithItem(cartItem, input.options);
          return result;
        }
        throw error;
      }
    } else {
      // Create new cart with item
      const result = await this.cartService.createCartWithItem(cartItem, input.options);
      return result;
    }
  }
}
